import React, { useState, useEffect } from 'react';
import { supabase } from '../../api/supabase';
import TypeSearch from './TypeSearch';

/**
 * Supplier search for a cost row, plus a sub-supplier dropdown when the chosen supplier
 * has suppliers linked under it (e.g. Arabian Lux under Rajabdeen Haji).
 *
 * The parent stays the row's supplier_id — it's who we owe, so the payable/AP and the
 * supplier ledger stay tagged to it. The sub-supplier is recorded for reference only
 * (sub_supplier_id / sub_supplier_label, and "PARENT › SUB" in the label, which flows into
 * the journal description). Picking a sub-supplier directly in the search resolves to its
 * parent the same way.
 *
 * Props: row ({ supplier_id, supplier_label, sub_supplier_id }), disabled, onChange(patch)
 */
export default function SupplierPicker({ row, disabled, onChange, placeholder = 'Search supplier…' }) {
  const [parent, setParent] = useState(null);   // { id, name }
  const [children, setChildren] = useState([]);

  // Show the top-level supplier even if the row's supplier_id is itself a sub-supplier
  // (older rows). The row isn't changed automatically — re-picking the supplier moves it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!row.supplier_id) { setParent(null); setChildren([]); return; }
      const { data: s } = await supabase.from('suppliers').select('id, name, parent_supplier_id').eq('id', row.supplier_id).maybeSingle();
      let top = s ? { id: s.id, name: s.name } : null;
      if (s?.parent_supplier_id) {
        const { data: p } = await supabase.from('suppliers').select('id, name').eq('id', s.parent_supplier_id).maybeSingle();
        if (p) top = p;
      }
      const { data: kids } = top
        ? await supabase.from('suppliers').select('id, name').eq('parent_supplier_id', top.id).eq('is_active', true).order('name')
        : { data: [] };
      if (cancelled) return;
      setParent(top);
      setChildren(kids || []);
    })();
    return () => { cancelled = true; };
  }, [row.supplier_id]);

  const parentName = parent?.name || (row.supplier_label || '').split(' › ')[0];

  return (
    <div>
      <TypeSearch
        fetchFn={async (q) => {
          const { data } = await supabase.from('suppliers').select('id, name, supplier_code, parent_supplier_id').eq('is_active', true).ilike('name', `%${q}%`).limit(20);
          return data || [];
        }}
        value={parentName || row.supplier_label}
        displayKey="name"
        placeholder={placeholder}
        disabled={disabled}
        onSelect={async sup => {
          // Picking a sub-supplier directly still makes its parent the one we owe.
          if (sup.parent_supplier_id) {
            const { data: p } = await supabase.from('suppliers').select('id, name').eq('id', sup.parent_supplier_id).maybeSingle();
            if (p) return onChange({ supplier_id: p.id, supplier_label: `${p.name} › ${sup.name}`, sub_supplier_id: sup.id, sub_supplier_label: sup.name });
          }
          onChange({ supplier_id: sup.id, supplier_label: sup.name, sub_supplier_id: null, sub_supplier_label: null });
        }}
        onClear={() => onChange({ supplier_id: '', supplier_label: '', sub_supplier_id: null, sub_supplier_label: null })}
      />
      {children.length > 0 && (
        <select className="form-input" style={{ marginTop: 6, fontSize: 12 }} disabled={disabled} value={row.sub_supplier_id || ''}
          onChange={e => {
            const kid = children.find(k => k.id === e.target.value);
            onChange(kid
              ? { supplier_label: `${parentName} › ${kid.name}`, sub_supplier_id: kid.id, sub_supplier_label: kid.name }
              : { supplier_label: parentName, sub_supplier_id: null, sub_supplier_label: null });
          }}>
          <option value="">{parentName} (direct)</option>
          {children.map(k => <option key={k.id} value={k.id}>Sub-supplier: {k.name}</option>)}
        </select>
      )}
    </div>
  );
}
